import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { loadPersistedConsent, persistConsent } from "./cookieConsent.storage";
import { type ConsentState, CONSENT_VERSION } from "./cookieConsent.types";

const persisted = loadPersistedConsent();

const initialState: ConsentState = {
  consentGiven: persisted.consentGiven ?? false,
  lastUpdated: persisted.lastUpdated ?? null,
  consentVersion: persisted.consentVersion ?? CONSENT_VERSION,
};

const cookieConsentSlice = createSlice({
  name: "cookieConsent",
  initialState,
  reducers: {
    /**
     * Accept the workspace cookie.
     * @param {ConsentState} state Slice draft state.
     * @param {PayloadAction<string>} action ISO timestamp payload.
     * @returns {void}
     */
    acceptConsent(state, action: PayloadAction<string>) {
      state.consentGiven = true;
      state.lastUpdated = action.payload;
      persistConsent(state);
    },

    /**
     * Hydrate local consent state from backend response.
     * @param {ConsentState} state Slice draft state.
     * @param {{ payload: { consentGiven: boolean; lastUpdated: string; consentVersion: string } }} action Backend payload.
     * @param action.payload Backend consent state.
     * @param action.payload.consentGiven Whether consent was accepted.
     * @param action.payload.lastUpdated Server consent update timestamp.
     * @param action.payload.consentVersion Accepted consent policy version.
     * @returns {void}
     */
    hydrateConsentFromServer(
      state,
      action: {
        payload: {
          consentGiven: boolean;
          lastUpdated: string;
          consentVersion: string;
        };
      },
    ) {
      state.consentGiven = action.payload.consentGiven;
      state.lastUpdated = action.payload.lastUpdated;
      state.consentVersion = action.payload.consentVersion;
      persistConsent(state);
    },
  },
});

export const { acceptConsent, hydrateConsentFromServer } =
  cookieConsentSlice.actions;

export default cookieConsentSlice.reducer;
